/**
 * The ArcGIS side of action symbology: turning the plain specs in
 * `actionSymbology.ts` into symbols, a renderer and a cluster configuration.
 *
 * Kept apart from the sync hook so the hook is about reconciliation and this is
 * about drawing, and so neither file has to know both.
 */

import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import {
  ACTION_SYMBOL_KEY_FIELD,
  actionPolygonSpec,
  actionSymbolKey,
  actionSymbolKeysInUse,
  actionSymbolSpec,
  type ActionSymbolFacets,
  type ActionSymbolSpec,
  type Rgba,
} from 'components/IAP/utils/actionSymbology';
import {
  getSymbolSvgUrl,
  isUscgSymbol,
} from 'components/MapPage/CorporateViewTabs/LayersTab/utils/uscgSymbology';

export function markerSymbolFor(spec: ActionSymbolSpec): SimpleMarkerSymbol {
  return new SimpleMarkerSymbol({
    style: 'circle',
    color: spec.color,
    size: spec.size,
    outline: { color: spec.outlineColor, width: spec.outlineWidth },
  });
}

/** CIM alpha is a 0-100 percentage, where the rest of the API uses 0-1. */
const cimColor = ([r, g, b, a]: Rgba): number[] => [r, g, b, a * 100];

/**
 * A circle as CIM geometry.
 *
 * CIM has no circle primitive; the documented form is a ring of two
 * semicircular arcs, each `[endPoint, centre, isMinor, isCounterClockwise]`.
 */
const cimCircle = (radius: number) => ({
  curveRings: [
    [
      [0, radius],
      { a: [[0, -radius], [0, 0], 0, 1] },
      { a: [[0, radius], [0, 0], 0, 1] },
    ],
  ],
});

/**
 * A chosen symbol drawn over the ring that carries its status.
 *
 * `PictureMarkerSymbol` has no outline or halo property, so the two cannot
 * simply be stacked; a composite CIM point symbol is the supported way. CIM
 * draws symbol layers top-first, hence the icon before the ring.
 *
 * Falls back to the plain circle for a key the catalog no longer knows, which
 * is the same forgiving behaviour `syncInternalLayers` has for stored keys.
 */
export function symbologyMarkerFor(
  symbology: string,
  spec: ActionSymbolSpec
): CIMSymbol | SimpleMarkerSymbol {
  if (!isUscgSymbol(symbology)) return markerSymbolFor(spec);

  // Priority still drives size, so a Critical task with an icon is still the
  // bigger thing on screen.
  const ringSize = spec.size + 16;
  const iconSize = spec.size + 6;
  const [r, g, b] = spec.color;

  return new CIMSymbol({
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMPointSymbol',
        symbolLayers: [
          {
            type: 'CIMPictureMarker',
            enable: true,
            size: iconSize,
            url: getSymbolSvgUrl(symbology),
          },
          {
            type: 'CIMVectorMarker',
            enable: true,
            size: ringSize,
            frame: { xmin: -5, ymin: -5, xmax: 5, ymax: 5 },
            markerGraphics: [
              {
                type: 'CIMMarkerGraphic',
                geometry: cimCircle(5),
                symbol: {
                  type: 'CIMPolygonSymbol',
                  symbolLayers: [
                    {
                      type: 'CIMSolidStroke',
                      enable: true,
                      width: spec.outlineWidth,
                      color: cimColor(spec.outlineColor),
                    },
                    {
                      // Translucent, so the icon on top stays legible while the
                      // status colour still reads at a glance.
                      type: 'CIMSolidFill',
                      enable: true,
                      color: cimColor([r, g, b, 0.35]),
                    },
                  ],
                },
              },
            ],
          },
        ],
      },
    },
  } as unknown as __esri.CIMSymbolProperties);
}

/** The symbol for one set of facets, chosen or derived. */
export function symbolForFacets(
  facets: ActionSymbolFacets
): CIMSymbol | SimpleMarkerSymbol {
  const spec = actionSymbolSpec(facets);
  return facets.symbology
    ? symbologyMarkerFor(facets.symbology, spec)
    : markerSymbolFor(spec);
}

export function fillSymbolFor(spec: ActionSymbolSpec): SimpleFillSymbol {
  return new SimpleFillSymbol({
    color: spec.color,
    outline: { color: spec.outlineColor, width: spec.outlineWidth },
  });
}

export function polygonSymbolForAction(
  facets: ActionSymbolFacets
): SimpleFillSymbol {
  return fillSymbolFor(actionPolygonSpec(facets));
}

/**
 * One entry per status/priority/overdue combination, plus one per chosen symbol
 * actually on screen.
 *
 * A FeatureLayer symbolises through a renderer rather than per-graphic symbols,
 * which is the price of clustering. Enumerating the combinations gets the
 * per-feature detail back: 26 derived ones, generated rather than written out,
 * and only the chosen symbols in use — the full cross product would be over a
 * thousand entries.
 *
 * Because the chosen half depends on the data, the caller has to reassign
 * `layer.renderer` when the set of symbols in use changes.
 */
export function actionPointRenderer(
  facetsInUse: readonly ActionSymbolFacets[] = []
): UniqueValueRenderer {
  const renderer = new UniqueValueRenderer({
    field: ACTION_SYMBOL_KEY_FIELD,
    defaultSymbol: markerSymbolFor(
      actionSymbolSpec({
        status: 'Unknown',
        priority: 'Normal',
        overdue: false,
        symbology: null,
      })
    ),
  });

  actionSymbolKeysInUse(facetsInUse).forEach((facets) => {
    renderer.addUniqueValueInfo({
      value: actionSymbolKey(facets),
      symbol: symbolForFacets(facets),
    });
  });

  return renderer;
}

/**
 * Cluster configuration.
 *
 * A few hundred tasks in one district was an unreadable blob; the cluster label
 * carries the count so the blob at least says how big it is. The radius is
 * large enough to actually collapse a dense district but small enough that two
 * neighbouring districts stay distinct.
 */
export function actionClusterReduction(): __esri.FeatureReductionCluster {
  return {
    type: 'cluster',
    clusterRadius: '60px',
    clusterMinSize: 20,
    clusterMaxSize: 44,
    labelingInfo: [
      {
        deconflictionStrategy: 'none',
        labelExpressionInfo: {
          expression: 'Text($feature.cluster_count, "#,###")',
        },
        symbol: new TextSymbol({
          color: [255, 255, 255, 1],
          font: { size: 11, weight: 'bold' },
          haloColor: [17, 24, 39, 1],
          haloSize: 1,
        }),
        labelPlacement: 'center-center',
      },
    ],
    popupEnabled: false,
  } as unknown as __esri.FeatureReductionCluster;
}
